import { createContext, useContext } from "react";

import { Box, BoxProps, Flex, Group, Text, TextProps, useMantineTheme } from "@mantine/core";
import { DefaultMantineColorName } from "app/ui/theme/types";
import _ from "lodash";
import { Cell, Pie, PieChart } from "recharts";

import { packSx } from "components/mantine-extended";

import { Strong } from "../strong";

type TPillContext = {
  color: DefaultMantineColorName;
};

const PillContext = createContext<TPillContext>({} as TPillContext);

type TPillProps = {
  color: DefaultMantineColorName;
} & BoxProps &
  React.ComponentPropsWithoutRef<"div">;

function PillAtom({ color, sx, children, ...boxProps }: TPillProps) {
  return (
    <Box
      display="inline-block"
      bg={color === "gray" ? "gray.2" : `${color}.1`}
      py={2}
      pl={2}
      pr={12}
      sx={[{ borderRadius: 1000 }, ...packSx(sx)]}
      {...boxProps}
    >
      <Group>{children}</Group>
    </Box>
  );
}

function PillLabel(textProps: TextProps) {
  const { color } = useContext(PillContext);
  return <Text fz="xs" c={`${color}.9`} {...textProps} />;
}

function Pill({ color, ...boxProps }: TPillProps) {
  return (
    <PillContext.Provider value={{ color }}>
      <PillAtom color={color} {...boxProps} />
    </PillContext.Provider>
  );
}
Pill.displayName = "StatusPill.Pill";

/*
 * Normally we'd try to break this component up into smaller pieces
 * but the thing is so small and has so much to it that instead, for
 * now, we just encapsulate a ton of stuff in this one complex component.
 *
 * If you're looking at this and trying to add additional props, it's
 * recommended that you consider breaking this up into a more compound
 * component first.
 */
function PillChart({
  percent: rawPercent,
  icon,
  ...boxProps
}: { percent: number; icon?: React.ReactNode } & BoxProps) {
  const theme = useMantineTheme();
  const { color } = useContext(PillContext);

  // It doesn't look good at 0 so we just get it goin...
  const percent = _.clamp(rawPercent, 7, 100);
  const roundedPercent = rawPercent === 0 ? "0" : rawPercent.toFixed(rawPercent < 1 ? 1 : 0);

  const data = [
    {
      value: percent,
    },
    {
      value: 100 - percent,
    },
  ];

  return (
    <Box display="inline-block" pos="relative" {...boxProps}>
      <PieChart width={32} height={32}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          startAngle={90}
          endAngle={-270}
          isAnimationActive={false}
          outerRadius={16}
          innerRadius={13}
        >
          {data.map((_datum, index) => (
            <Cell
              key={`cell-${index}`}
              fill={index === 0 ? theme.colors[color][9] : theme.colors.gray[3]}
              strokeWidth={0}
            />
          ))}
        </Pie>
        <Pie
          data={[{ value: 100 }]}
          dataKey="value"
          nameKey="name"
          startAngle={90}
          endAngle={-270}
          isAnimationActive={false}
          outerRadius={13}
          innerRadius={0}
          fill="white"
        />
      </PieChart>
      <Flex h="100%" w="100%" pos="absolute" top={0} left={0} justify="center" align="center">
        <Text fz="xs" c={`${color}.9`}>
          {icon ? (
            <Flex>{icon}</Flex>
          ) : (
            <Strong>
              <span style={{ verticalAlign: "center" }}>{roundedPercent}</span>
              <span style={{ verticalAlign: "super", fontSize: 6 }}>%</span>
            </Strong>
          )}
        </Text>
      </Flex>
    </Box>
  );
}

export const StatusPill = {
  Pill: Pill,
  Label: PillLabel,
  Chart: PillChart,
};
